// Equity calculator widget component
import React from 'react'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { EquityCalculationEngine } from '@/lib/equity-calculator-v2'
import { EquityCalculationResult } from '@/types/equity-calculator'
import { useProject } from '@/contexts/project-context'
import { Button } from '@/components/ui/button'
import { Calculator, RefreshCw, Save } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface EquityCalculatorWidgetProps {
  onSuccess?: () => void
}

export default function EquityCalculatorWidget({ onSuccess }: EquityCalculatorWidgetProps) {
  const [open, setOpen] = useState(false)
  const [result, setResult] = useState<EquityCalculationResult | null>(null)
  const queryClient = useQueryClient()
  const { currentProject } = useProject()

  const { mutate: calculate, isPending: isCalculating } = useMutation({
    mutationFn: async ({ projectId }: { projectId: string }) => {
      const engine = new EquityCalculationEngine(projectId)
      return engine.calculateEquityAllocations()
    },
    onSuccess: (data) => {
      setResult(data)
    },
    onError: (error) => {
      console.error('Calculation failed:', error)
    },
  })

  const { mutate: saveToCapTable, isPending: isSaving } = useMutation({
    mutationFn: async ({ projectId, result }: { projectId: string; result: EquityCalculationResult }) => {
      const engine = new EquityCalculationEngine(projectId)
      await engine.updateCapTable(result.allocations)
      await engine.saveCalculationHistory(result, 'widget', 'Updated from equity calculator widget')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cap-table'] })
      setOpen(false)
      onSuccess?.()
    },
    onError: (error) => {
      console.error('Failed to update cap table:', error)
    },
  })

  const handleCalculate = () => {
    if (!currentProject) return
    setOpen(true)
    calculate({ projectId: currentProject.id })
  }

  const handleSave = () => {
    if (result && currentProject) {
      saveToCapTable({ projectId: currentProject.id, result })
    }
  }

  return (
    <>
      <Button onClick={handleCalculate} className="bg-purple-600 hover:bg-purple-700">
        <Calculator className="h-4 w-4 mr-2" />
        Calculate Equity
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Equity Calculation Preview</DialogTitle>
            <DialogDescription>
              Based on all contributions and task weights. Review before updating the cap table.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[400px] overflow-y-auto">
            {isCalculating ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : result ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500">Founder Ownership</p>
                    <p className="text-lg font-semibold">{result.founder_ownership.toFixed(1)}%</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500">Investor Ownership</p>
                    <p className="text-lg font-semibold">{result.investor_ownership.toFixed(1)}%</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500">Option Pool</p>
                    <p className="text-lg font-semibold">{result.option_pool}%</p>
                  </div>
                </div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contributor
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Score
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Equity %
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Shares
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {result.allocations.map((allocation) => (
                      <tr key={allocation.user_id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {allocation.user_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {allocation.contribution_score.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                          {allocation.equity_percentage.toFixed(2)}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {allocation.shares.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">Total</td>
                      <td className="px-6 py-3 text-sm text-gray-500 text-right">
                        {result.total_score.toFixed(0)}
                      </td>
                      <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">
                        {result.allocations.reduce((sum, a) => sum + a.equity_percentage, 0).toFixed(2)}%
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-500 text-right">
                        {result.allocations.reduce((sum, a) => sum + a.shares, 0).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-center py-8 text-gray-500">No data available</p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isCalculating || isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isCalculating || isSaving || !result}
            >
              {isSaving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Update Cap Table
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}